import React, { useState, useEffect } from 'react';
import { useNavigate } from '../utils/SimpleRouter';
import { ArrowLeft, Check, Globe } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './OrderForm.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderForm = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    orgNumber: '',
    invoiceEmail: '',
    invoiceAddress: '',
    invoicePostalCode: '',
    invoiceCity: '',
    invoiceCountry: 'Norge',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    numberOfEntrances: '',
    orderType: 'demo_only',
    quantity: 'demo_first',
    language: 'no',
    needsScreen: 'no',
    screenOrientation: 'portrait',
    additionalInfo: ''
  });

  // Auto-detect browser language on mount (only if no saved preference)
  useEffect(() => {
    // Check if user has already set a language preference
    const savedLang = localStorage.getItem('i18nextLng');
    
    // Only auto-detect if no saved preference
    if (!savedLang) {
      const browserLang = navigator.language || navigator.userLanguage;
      let detectedLang = 'no'; // Default to Norwegian
      
      if (browserLang) {
        const langCode = browserLang.toLowerCase().split('-')[0];
        // Map browser language to our supported languages
        const langMap = {
          'en': 'en',
          'no': 'no',
          'nb': 'no',
          'nn': 'no',
          'sv': 'sv',
          'da': 'dk',
          'dk': 'dk',
          'pl': 'pl',
          'de': 'de'
        };
        detectedLang = langMap[langCode] || 'no';
      }
      
      // Set i18n language based on browser (for UI translation)
      const uiLang = detectedLang === 'dk' ? 'no' : detectedLang; // Danish users see Norwegian UI (similar)
      if (uiLang === 'en' || uiLang === 'no') {
        i18n.changeLanguage(uiLang);
      }
      
      // Also set the form's preferred language
      setFormData(prev => ({
        ...prev,
        language: detectedLang
      }));
    }
  }, [i18n]);

  // Language switcher for UI
  const toggleLanguage = () => {
    const currentLang = localStorage.getItem('i18nextLng') || i18n.language || 'en';
    const newLang = currentLang === 'no' || currentLang === 'nb' ? 'en' : 'no';
    
    // Save to localStorage first (before reload)
    localStorage.setItem('i18nextLng', newLang);
    // Reload to apply new language
    window.location.reload();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit order to backend
      await axios.post(`${API}/orders`, formData);
      
      toast.success(t('orderForm.successMessage'));
      
      // Reset form
      setFormData({
        companyName: '',
        orgNumber: '',
        invoiceEmail: '',
        invoiceAddress: '',
        invoicePostalCode: '',
        invoiceCity: '',
        invoiceCountry: 'Norge',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        numberOfEntrances: '',
        orderType: 'demo_only',
        quantity: 'demo_first',
        language: 'no',
        needsScreen: 'no',
        screenOrientation: 'portrait',
        additionalInfo: ''
      });

      // Show success message for 3 seconds then redirect
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (error) {
      console.error('Order submission error:', error);
      // Show specific error message from backend if available
      const errorMessage = error.response?.data?.detail || t('orderForm.errorMessage');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="order-page">
      {/* Header */}
      <nav className="order-nav">
        <div className="nav-container">
          <a href="https://infovegg.no" className="back-button" style={{ textDecoration: 'none', color: 'inherit' }}>
            <ArrowLeft size={20} />
            {t('orderForm.back')}
          </a>
          <div className="logo">
            <img src="/infovegg-logo.svg" alt="Infovegg" style={{ height: '40px', width: 'auto' }} />
          </div>
          {/* Language toggle button */}
          <button 
            type="button"
            onClick={toggleLanguage} 
            className="language-toggle-btn"
            title={i18n.language === 'no' ? 'Switch to English' : 'Bytt til norsk'}
          >
            <Globe size={18} />
            <span>{i18n.language === 'no' || i18n.language === 'nb' ? 'EN' : 'NO'}</span>
          </button>
        </div>
      </nav>

      {/* Order Form */}
      <section className="order-section">
        <div className="order-container">
          <div className="order-header">
            <h1>{t('orderForm.pageTitle')}</h1>
            <p>{t('orderForm.pageSubtitle')}</p>
          </div>

          <div className="order-content">
            {/* Form */}
            <form className="order-form" onSubmit={handleSubmit}>
              {/* Company Information */}
              <div className="form-section">
                <h2>{t('orderForm.companyInfo')}</h2>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('orderForm.companyName')} *</label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      placeholder={t('orderForm.companyNamePlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('orderForm.orgNumber')} *</label>
                    <input
                      type="text"
                      name="orgNumber"
                      value={formData.orgNumber}
                      onChange={handleChange}
                      required
                      placeholder={t('orderForm.orgNumberPlaceholder')}
                    />
                  </div>
                </div>
              </div>

              {/* Invoice Information */}
              <div className="form-section">
                <h2>{t('orderForm.invoiceInfo')}</h2>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem', fontStyle: 'italic' }}>
                  {t('orderForm.invoiceInfoNote')}
                </p>
                <div className="form-group">
                  <label>{t('orderForm.invoiceEmail')}</label>
                  <input
                    type="email"
                    name="invoiceEmail"
                    value={formData.invoiceEmail}
                    onChange={handleChange}
                    placeholder={t('orderForm.invoiceEmailPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('orderForm.invoiceAddress')}</label>
                  <input
                    type="text"
                    name="invoiceAddress"
                    value={formData.invoiceAddress}
                    onChange={handleChange}
                    placeholder={t('orderForm.invoiceAddressPlaceholder')}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('orderForm.postalCode')}</label>
                    <input
                      type="text"
                      name="invoicePostalCode"
                      value={formData.invoicePostalCode}
                      onChange={handleChange}
                      placeholder={t('orderForm.postalCodePlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('orderForm.city')}</label>
                    <input
                      type="text"
                      name="invoiceCity"
                      value={formData.invoiceCity}
                      onChange={handleChange}
                      placeholder={t('orderForm.cityPlaceholder')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('orderForm.country')}</label>
                  <select
                    name="invoiceCountry"
                    value={formData.invoiceCountry}
                    onChange={handleChange}
                  >
                    <option value="Norge">{t('orderForm.countryNorway')}</option>
                    <option value="Sverige">{t('orderForm.countrySweden')}</option>
                    <option value="Danmark">{t('orderForm.countryDenmark')}</option>
                    <option value="Finland">{t('orderForm.countryFinland')}</option>
                    <option value="Island">{t('orderForm.countryIceland')}</option>
                    <option value="Tyskland">{t('orderForm.countryGermany')}</option>
                    <option value="Nederland">{t('orderForm.countryNetherlands')}</option>
                    <option value="Belgia">{t('orderForm.countryBelgium')}</option>
                    <option value="Storbritannia">{t('orderForm.countryUK')}</option>
                    <option value="Frankrike">{t('orderForm.countryFrance')}</option>
                    <option value="Spania">{t('orderForm.countrySpain')}</option>
                    <option value="Italia">{t('orderForm.countryItaly')}</option>
                    <option value="Polen">{t('orderForm.countryPoland')}</option>
                    <option value="Østerrike">{t('orderForm.countryAustria')}</option>
                    <option value="Sveits">{t('orderForm.countrySwitzerland')}</option>
                    <option value="USA">{t('orderForm.countryUSA')}</option>
                    <option value="Annet">{t('orderForm.countryOther')}</option>
                  </select>
                </div>
              </div>

              {/* Contact Information */}
              <div className="form-section">
                <h2>{t('orderForm.contactPerson')}</h2>
                <div className="form-group">
                  <label>{t('orderForm.contactName')} *</label>
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    required
                    placeholder={t('orderForm.contactNamePlaceholder')}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('orderForm.contactEmail')} *</label>
                    <input
                      type="email"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleChange}
                      required
                      placeholder={t('orderForm.contactEmailPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('orderForm.contactPhone')} *</label>
                    <input
                      type="tel"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleChange}
                      required
                      placeholder={t('orderForm.contactPhonePlaceholder')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('orderForm.numberOfEntrances')} *</label>
                  <input
                    type="number"
                    name="numberOfEntrances"
                    value={formData.numberOfEntrances}
                    onChange={handleChange}
                    required
                    placeholder={t('orderForm.numberOfEntrancesPlaceholder')}
                    min="1"
                  />
                </div>
              </div>

              {/* Order Type Selection */}
              <div className="form-section">
                <h2>{t('orderForm.orderType')}</h2>
                <div className="form-group">
                  <label>{t('orderForm.orderTypeLabel')} *</label>
                  <select
                    name="orderType"
                    value={formData.orderType}
                    onChange={handleChange}
                    required
                    style={{ fontSize: '1rem', padding: '1rem' }}
                  >
                    <option value="demo_only">{t('orderForm.orderTypeDemoOnly')}</option>
                    <option value="subscription_only">{t('orderForm.orderTypeSubscriptionOnly')}</option>
                    <option value="infovegg_with_screen">{t('orderForm.orderTypeWithScreen')}</option>
                    <option value="other">{t('orderForm.orderTypeOther')}</option>
                  </select>
                </div>
              </div>

              {/* Subscription Details */}
              <div className="form-section">
                <h2>{t('orderForm.subscription')}</h2>
                <div className="form-group">
                  <label>{t('orderForm.quantityLabel')} *</label>
                  <select
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    required
                  >
                    <option value="1">{t('orderForm.quantity1')}</option>
                    <option value="2">{t('orderForm.quantity2')}</option>
                    <option value="3">{t('orderForm.quantity3')}</option>
                    <option value="4">{t('orderForm.quantity4')}</option>
                    <option value="5">{t('orderForm.quantity5')}</option>
                    <option value="6">{t('orderForm.quantity6')}</option>
                    <option value="7+">{t('orderForm.quantity7plus')}</option>
                    <option value="multiple_quote">{t('orderForm.quantityMultiple')}</option>
                    <option value="demo_first">{t('orderForm.quantityDemo')}</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>{t('orderForm.preferredLanguage')} *</label>
                  <select
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    required
                  >
                    <option value="no">{t('orderForm.languageNorwegian')}</option>
                    <option value="en">{t('orderForm.languageEnglish')}</option>
                    <option value="sv">{t('orderForm.languageSwedish')}</option>
                    <option value="dk">{t('orderForm.languageDanish')}</option>
                    <option value="pl">{t('orderForm.languagePolish')}</option>
                    <option value="de">{t('orderForm.languageGerman')}</option>
                  </select>
                  <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                    {t('orderForm.languageHelp')}
                  </small>
                </div>
              </div>

              {/* Hardware Selection - Only show if demo_with_screens */}
              {formData.orderType === 'demo_with_screens' && (
                <div className="form-section">
                  <h2>Skjermbestilling</h2>
                  <div className="hardware-info-box">
                    <p><strong>LG 32" profesjonell skjerm:</strong> 5.990,- per skjerm (eks. mva/frakt)</p>
                    <p>Ferdig oppsatt og klar til bruk (plug & play)</p>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Hvor mange skjermer ønsker dere å bestille? *</label>
                      <select
                        name="needsScreen"
                        value={formData.needsScreen}
                        onChange={handleChange}
                        required={formData.orderType === 'demo_with_screens'}
                      >
                        <option value="">Velg antall</option>
                        <option value="1">1 skjerm - 5.990,- eks. mva/frakt</option>
                        <option value="2">2 skjermer - 11.980,- eks. mva/frakt</option>
                        <option value="3">3 skjermer - 17.970,- eks. mva/frakt</option>
                        <option value="4">4 skjermer - 23.960,- eks. mva/frakt</option>
                        <option value="5">5 skjermer - 29.950,- eks. mva/frakt</option>
                        <option value="6">6 skjermer - 35.940,- eks. mva/frakt</option>
                        <option value="custom">Annet antall/andre størrelser (spesifiser i merknad)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Orientering *</label>
                      <select
                        name="screenOrientation"
                        value={formData.screenOrientation}
                        onChange={handleChange}
                        required={formData.orderType === 'demo_with_screens'}
                      >
                        <option value="portrait">Alle stående</option>
                        <option value="landscape">Alle liggende</option>
                        <option value="mixed">Miks av stående og liggende (spesifiser i merknad)</option>
                      </select>
                    </div>
                  </div>
                  <div className="disclaimer-note">
                    <strong>⚠️ Viktig:</strong>
                    <ul>
                      <li>Etablering av strøm og internett må gjøres av elektriker (ikke inkludert)</li>
                      <li>Oppmontering på vegg er ikke inkludert</li>
                      <li>Veggfeste følger med hver skjerm</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Additional Information */}
              <div className="form-section">
                <h2>{t('orderForm.additionalInfo')}</h2>
                <div className="form-group">
                  <label>{t('orderForm.additionalInfoLabel')}</label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    rows="4"
                    placeholder={t('orderForm.additionalInfoPlaceholder')}
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="form-submit">
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('orderForm.submitting') : t('orderForm.submit')}
                </button>
                <p className="form-note">
                  {t('orderForm.submitNote')}
                </p>
              </div>
            </form>

            {/* Order Summary */}
            <div className="order-summary">
              <h3>{t('orderForm.summaryTitle')}</h3>
              <div className="summary-items">
                <div className="summary-item">
                  <Check size={18} className="check-icon" />
                  <span>{t('orderForm.summaryTrial')}</span>
                </div>
                <div className="summary-item">
                  <Check size={18} className="check-icon" />
                  <span>{t('orderForm.summaryAccess')}</span>
                </div>
                <div className="summary-item">
                  <Check size={18} className="check-icon" />
                  <span>{t('orderForm.summaryAllFeatures')}</span>
                </div>
                <div className="summary-item">
                  <Check size={18} className="check-icon" />
                  <span>{t('orderForm.summaryNoBinding')}</span>
                </div>
                <div className="summary-item">
                  <Check size={18} className="check-icon" />
                  <span>{t('orderForm.summaryOwnScreen')}</span>
                </div>
              </div>

              <div className="price-breakdown">
                <div className="price-row">
                  <span>{t('orderForm.afterTrial')}</span>
                  <strong>{t('orderForm.pricePerMonth')}</strong>
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginTop: '0.5rem', textAlign: 'center' }}>
                  {t('orderForm.exclVat')}
                </p>
              </div>

              <div className="summary-note">
                <strong>{t('orderForm.bonus')}</strong> {t('orderForm.bonusText')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="order-footer">
        <div className="footer-container">
          <p>{t('orderForm.footerText')} <a href="https://displaysystem.no" target="_blank" rel="noopener noreferrer">Displaysystem.no</a></p>
        </div>
      </footer>
    </div>
  );
};

export default OrderForm;
